import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { Camera, FileText, Users, ClipboardCheck, CheckCircle2, Phone, Mail, Shield, Home, Building2, AlertCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FloatingCTAs from '../components/FloatingCTAs';
import AddressAutocomplete from '../components/AddressAutocomplete';
import ImageUploader from '../components/ImageUploader';

export default function InsuranceClaims() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    claim_type: '',
    message: ''
  });
  const [photos, setPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const fd = new FormData();
      // Map "address" to remain consistent across forms (backend accepts address).
      Object.entries(formData).forEach(([k, v]) => {
        if (v !== null && v !== undefined) fd.append(k, String(v));
      });
      (photos || []).slice(0, 5).forEach((file) => fd.append('photos', file));

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/insurance-claim/submit`, {
        method: 'POST',
        body: fd,
      });

      if (response.ok) {
        toast.success('Thank you! We will review your claim details and contact you within 24 hours.');
        setFormData({ name: '', phone: '', email: '', address: '', city: '', state: '', zip_code: '', claim_type: '', message: '' });
        setPhotos([]);
      } else {
        toast.error('Something went wrong. Please call us directly at (407) 785-0642.');
      }
    } catch (error) {
      toast.error('Failed to submit. Please call us at (407) 785-0642.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[500px] flex items-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-block mb-6">
              <span className="px-4 py-2 bg-[hsl(var(--accent))] text-white text-sm font-bold uppercase tracking-wide rounded-full">
                Expert Insurance Support
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Insurance Claims Support for Roofing & Exterior Property Repairs
            </h1>
            <p className="text-xl sm:text-2xl text-slate-200 mb-8 leading-relaxed">
              We help homeowners, HOAs, property managers, CAMs, and business owners navigate insurance claims for roofing, storm damage, and exterior repairs with clarity and confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={scrollToForm}
                className="h-14 px-8 bg-[hsl(var(--accent))] hover:bg-[#a01830] text-white text-lg font-bold shadow-xl"
                data-testid="free-claim-review-btn"
              >
                Free Insurance Claim Review
              </Button>
              <Button 
                onClick={scrollToForm}
                variant="outline"
                className="h-14 px-8 bg-white hover:bg-slate-50 text-slate-900 text-lg font-semibold border-2"
                data-testid="start-claim-assistance-btn"
              >
                Start Your Claim Assistance
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Insurance Claims Often Matter */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Why Insurance Claims Often Matter
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Understanding when and why to file a claim can save you thousands and protect your property investment.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              { icon: <AlertCircle className="h-8 w-8" />, title: 'Weather Damage', desc: 'Wind, hail, and storm damage can compromise your roof integrity. Insurance often covers these unexpected events when documented properly.' },
              { icon: <Home className="h-8 w-8" />, title: 'Aging Roofs & Leaks', desc: 'Older roofs may qualify for coverage when storm damage accelerates existing wear. Proper documentation is key to approval.' },
              { icon: <Building2 className="h-8 w-8" />, title: 'Exterior Damage Claims', desc: 'Beyond roofing, insurance can cover stucco damage, fascia repair, and other exterior issues caused by covered perils.' },
              { icon: <FileText className="h-8 w-8" />, title: 'Common Insurer Confusion', desc: 'Insurers may underestimate damage scope or miss hidden issues. Our detailed documentation ensures nothing is overlooked.' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-14 h-14 rounded-full bg-blue-50 text-[hsl(var(--primary))] flex items-center justify-center">
                        {item.icon}
                      </div>
                      <div>
                        <CardTitle className="text-xl mb-2">{item.title}</CardTitle>
                        <CardDescription className="text-slate-600">{item.desc}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Claims Assistance Services */}
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Our Claims Assistance Services
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We guide you through every step of the claims process with expert support and documentation.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { 
                icon: <Camera className="h-8 w-8" />, 
                num: '1',
                title: 'Insurance Documentation & Evidence Gathering', 
                items: ['On-site inspection photos and videos', 'Written scope reports ready for insurer review', 'Detailed damage documentation']
              },
              { 
                icon: <FileText className="h-8 w-8" />, 
                num: '2',
                title: 'Claim Preparation & Submission Support', 
                items: ['Help prepare clear damage descriptions', 'Work directly with adjusters', 'Ensure nothing is missed']
              },
              { 
                icon: <Users className="h-8 w-8" />, 
                num: '3',
                title: 'Adjuster Accompaniment', 
                items: ['Attend adjuster visits to document damage', 'Provide professional testimony on severity', 'Ensure fair assessment']
              },
              { 
                icon: <ClipboardCheck className="h-8 w-8" />, 
                num: '4',
                title: 'Scope Review & Estimate Alignment', 
                items: ['Review insurer Scope of Loss vs real needs', 'Provide supplemental documentation', 'Advocate for complete repairs']
              },
              { 
                icon: <CheckCircle2 className="h-8 w-8" />, 
                num: '5',
                title: 'Project Execution After Approval', 
                items: ['Fast repair or replacement once approved', 'All work coordinated through Elite Roofing', 'Quality assurance and warranty support']
              }
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full">
                  <CardHeader>
                    <div className="flex items-start gap-4 mb-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[hsl(var(--primary))] text-white flex items-center justify-center text-xl font-bold">
                        {service.num}
                      </div>
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-50 text-[hsl(var(--primary))] flex items-center justify-center">
                        {service.icon}
                      </div>
                    </div>
                    <CardTitle className="text-xl mb-3">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <ul className="space-y-2">
                      {service.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-slate-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How the Insurance Claims Process Works */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              How the Insurance Claims Process Works
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              A clear, step-by-step guide to navigating your roofing or exterior insurance claim.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              { step: '1', title: 'Inspection & Damage Assessment', desc: 'We visit your property to document all damage with photos, videos, and detailed notes. This creates the foundation for your claim.' },
              { step: '2', title: 'Insurance Claim Initiation', desc: 'You file the claim with your insurance company. We provide you with all documentation needed to support your submission.' },
              { step: '3', title: 'Meeting with Adjuster', desc: 'The insurance adjuster inspects your property. We can accompany the adjuster to ensure all damage is identified and documented.' },
              { step: '4', title: 'Documentation Review', desc: 'We review the adjuster report and Scope of Loss. If damage was missed or undervalued, we prepare supplemental documentation.' },
              { step: '5', title: 'Approval & Scheduling', desc: 'Once your claim is approved, we help you understand your coverage, deductible, and next steps.' },
              { step: '6', title: 'Repair or Replacement Execution', desc: 'We complete all approved work efficiently, keeping you updated and ensuring quality results backed by warranty.' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="border-l-4 border-[hsl(var(--accent))]">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-14 h-14 rounded-full bg-[hsl(var(--primary))] text-white flex items-center justify-center text-2xl font-bold">
                        {item.step}
                      </div>
                      <div>
                        <CardTitle className="text-xl mb-2">{item.title}</CardTitle>
                        <CardDescription className="text-slate-600 text-base">{item.desc}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Help */}
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Who We Help
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Expert insurance claims assistance for residential and commercial properties across Central Florida.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: <Home className="h-8 w-8" />, title: 'Homeowners', desc: 'Navigate your first or complex claim with expert guidance and documentation support' },
              { icon: <Users className="h-8 w-8" />, title: 'HOAs & Community Associations', desc: 'Board-ready documentation and claims coordination for community-wide damage' },
              { icon: <Building2 className="h-8 w-8" />, title: 'Property Managers (CAMs/LCAMs)', desc: 'Streamlined claims process with professional documentation for your properties' },
              { icon: <Building2 className="h-8 w-8" />, title: 'Commercial Property Owners', desc: 'Complex commercial claims handled with detailed scope reports and coordination' },
              { icon: <Building2 className="h-8 w-8" />, title: 'Multi-Family / Condominium Complexes', desc: 'Experience with large-scale claims affecting multiple units or buildings' }
            ].map((client, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="mb-4 inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-50 text-[hsl(var(--primary))]">
                      {client.icon}
                    </div>
                    <CardTitle className="text-lg mb-2">{client.title}</CardTitle>
                    <CardDescription className="text-slate-600">{client.desc}</CardDescription>
                  </CardHeader>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mt-12 text-center"
          >
            <div className="inline-block bg-[hsl(var(--primary))] text-white rounded-xl p-8 shadow-xl">
              <h3 className="text-2xl font-semibold mb-4">Our Insurance Claims Experience</h3>
              <p className="text-slate-100 max-w-2xl">
                We have extensive experience working with insurance adjusters, understanding policy coverage, and preparing documentation that helps expedite approvals. From residential storm damage to commercial property claims, we have helped hundreds of clients successfully navigate the claims process.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ - Claims Focused */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-slate-600">
              Clear answers to common insurance claims questions
            </p>
          </motion.div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="bg-white rounded-lg border px-6">
              <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-[hsl(var(--primary))]">
                Do I need to file a claim if I see damage?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 pt-2">
                Not all damage requires a claim. We recommend getting a free inspection first to assess the extent and cause of damage. If the damage is storm-related and exceeds your deductible, filing a claim is often the right choice. We will help you understand your options before you file.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-white rounded-lg border px-6">
              <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-[hsl(var(--primary))]">
                Will filing a claim raise my rates?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 pt-2">
                Filing a claim may affect your premium, especially if you have had multiple claims. However, if you have storm damage covered under your policy, not filing means paying out-of-pocket for expensive repairs. We can help you evaluate the cost-benefit before you decide.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-white rounded-lg border px-6">
              <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-[hsl(var(--primary))]">
                What if the insurer denies part of the claim?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 pt-2">
                Partial denials are common. We provide supplemental documentation, additional photos, and professional assessments that can support an appeal. Many initially denied items get approved when backed by proper evidence.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-white rounded-lg border px-6">
              <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-[hsl(var(--primary))]">
                Do I have to use my deductible?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 pt-2">
                Yes, if your claim is approved, you will be responsible for your policy deductible. The insurance company covers the remaining approved costs. We provide transparent estimates so you know exactly what to expect.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="bg-white rounded-lg border px-6">
              <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-[hsl(var(--primary))]">
                How long do claims normally take?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 pt-2">
                Timelines vary by insurer and claim complexity. Simple claims may take 2-4 weeks, while complex or disputed claims can take longer. We help expedite the process by providing complete, accurate documentation upfront and responding quickly to adjuster requests.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Trust & Social Proof */}
      <section className="py-16 md:py-24 bg-[hsl(var(--primary))] text-white">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Trusted Insurance Claims Partner
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: 'Robert K.', role: 'Homeowner', location: 'Orlando', text: 'Elite Roofing handled our entire insurance claim after Hurricane damage. They documented everything, met with the adjuster, and our claim was approved in full. The repair work was completed quickly and professionally.' },
              { name: 'Linda S.', role: 'HOA Board President', location: 'Kissimmee', text: 'Working with Elite Roofing on our community-wide roof claim was seamless. Their documentation was board-ready, and they helped us navigate the insurer requirements. Highly recommend for association claims.' },
              { name: 'David M.', role: 'Property Manager', location: 'Winter Garden', text: 'I have used Elite Roofing for multiple insurance claims across different properties. They understand what adjusters look for and provide the documentation needed to get claims approved without delays.' }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full bg-white/10 border-white/20 text-white">
                  <CardContent className="p-6">
                    <p className="text-slate-100 mb-4 italic leading-relaxed">{testimonial.text}</p>
                    <div className="border-t border-white/20 pt-4">
                      <p className="font-semibold">{testimonial.name}</p>
                      <p className="text-sm text-slate-200">{testimonial.role} • {testimonial.location}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-12 text-center"
          >
            <div className="inline-flex flex-wrap justify-center gap-6 bg-white/10 rounded-xl p-8">
              <div className="flex items-center gap-3">
                <Shield className="h-8 w-8" />
                <div className="text-left">
                  <div className="font-bold text-lg">Licensed & Insured</div>
                  <div className="text-sm text-slate-200">Fully compliant contractors</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8" />
                <div className="text-left">
                  <div className="font-bold text-lg">Expert Documentation</div>
                  <div className="text-sm text-slate-200">Claims-ready reports</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-8 w-8" />
                <div className="text-left">
                  <div className="font-bold text-lg">Proven Track Record</div>
                  <div className="text-sm text-slate-200">Hundreds of successful claims</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Need Help with an Insurance Claim?
            </h2>
            <p className="text-xl text-slate-600">
              Request Your Free Insurance Claim Review Today
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="shadow-2xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-sm font-semibold text-slate-700 mb-2 block">
                        Full Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Your full name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="h-12"
                        data-testid="insurance-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-sm font-semibold text-slate-700 mb-2 block">
                        Phone <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="(407) 555-1234"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        className="h-12"
                        data-testid="insurance-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-sm font-semibold text-slate-700 mb-2 block">
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="h-12"
                      data-testid="insurance-email-input"
                    />
                  </div>

                  <AddressAutocomplete
                    value={formData.address}
                    onChange={(text) => setFormData((s) => ({ ...s, address: text }))}
                    onPlaceSelected={(p) => setFormData((s) => ({
                      ...s,
                      address: p.formatted || p.street || s.address,
                      city: p.city || s.city,
                      state: p.state || s.state,
                      zip_code: p.zip_code || s.zip_code,
                    }))}
                    label="Property Address"
                    required={false}
                    testId="insurance-address-input"
                    placeholder="Start typing the property address..."
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-2 block">City</Label>
                      <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="h-12" placeholder="City" data-testid="insurance-city-input" />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-2 block">State</Label>
                      <Input value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })} maxLength={2} className="h-12" placeholder="FL" data-testid="insurance-state-input" />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-2 block">Zip Code</Label>
                      <Input value={formData.zip_code} onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })} className="h-12" placeholder="ZIP" data-testid="insurance-zip-input" />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="claim_type" className="text-sm font-semibold text-slate-700 mb-2 block">
                      Claim Type
                    </Label>
                    <Select
                      value={formData.claim_type}
                      onValueChange={(value) => setFormData({ ...formData, claim_type: value })}
                    >
                      <SelectTrigger className="h-12" data-testid="insurance-claim-type-select">
                        <SelectValue placeholder="Select claim type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="storm-damage">Storm Damage (Wind/Hail)</SelectItem>
                        <SelectItem value="hurricane-damage">Hurricane Damage</SelectItem>
                        <SelectItem value="roof-leak">Roof Leak</SelectItem>
                        <SelectItem value="wind-damage">Wind Damage</SelectItem>
                        <SelectItem value="hail-damage">Hail Damage</SelectItem>
                        <SelectItem value="water-damage">Water Damage</SelectItem>
                        <SelectItem value="tree-damage">Tree Fall Damage</SelectItem>
                        <SelectItem value="stucco-damage">Stucco/Exterior Damage</SelectItem>
                        <SelectItem value="multiple">Multiple Issues</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-sm font-semibold text-slate-700 mb-2 block">
                      Describe the Damage
                    </Label>
                    <Textarea
                      id="message"
                      placeholder="Please describe what happened and what damage you have observed..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={5}
                      className="resize-none"
                      data-testid="insurance-message-textarea"
                    />
                  </div>

                  <ImageUploader
                    files={photos}
                    onChange={setPhotos}
                    maxFiles={5}
                    maxMb={10}
                    label="Attach photos of the damage (optional)"
                    hint="Photos help us assess the claim faster. Up to 5 images, 10MB each."
                    testId="insurance-photos"
                  />

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-14 bg-[hsl(var(--accent))] hover:bg-[#a01830] text-white text-lg font-bold shadow-xl"
                    data-testid="insurance-submit-btn"
                  >
                    {isSubmitting ? 'Submitting...' : 'Request Free Claim Review'}
                  </Button>

                  <div className="text-center space-y-3 pt-4 border-t border-slate-200">
                    <p className="text-sm text-slate-600">Or contact us directly:</p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                      <a href="tel:+14077850642" className="flex items-center gap-2 text-[hsl(var(--primary))] hover:underline font-semibold">
                        <Phone className="h-4 w-4" />
                        (407) 785-0642
                      </a>
                      <a href="mailto:jeremy@elitercfl.com" className="flex items-center gap-2 text-[hsl(var(--primary))] hover:underline font-semibold">
                        <Mail className="h-4 w-4" />
                        jeremy@elitercfl.com
                      </a>
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      <Footer />
      <FloatingCTAs />
    </div>
  );
}
